import { removeFreshLeadPool,clearAllLeadPoolFreshLeads  } from '../../../../redux/freshLeadPoolSlice';
import { useTierCountdown } from '../useTierCountdown';
import { toast } from 'react-toastify';

import {
	VStack,
	HStack,
	Badge,
	Progress,
	Button,
	Box,
	Icon,
	Heading,
	Text,
	Tooltip,
	SimpleGrid,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
} from '@chakra-ui/react';
import {
	FiClock,
	FiAward,
	FiDollarSign,
	FiTarget,
	FiGlobe,
	FiX,
	FiZap,
} from 'react-icons/fi';
import { useDispatch,useSelector  } from 'react-redux';
import { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCoins, FaStar, FaUser } from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

const FreshLeadPoolCard = ({ lead, isSubmitting, handleSubmit }) => {
	const dispatch = useDispatch();
const leads = useSelector((state) => state.freshLeadPool?.leads ?? []);
	const hasMultipleLeads = leads.length > 1;
	const handleExpire = useCallback(() => {
		dispatch(removeFreshLeadPool(lead._id));
		toast.info('Lead request expired!');
	}, [dispatch]);

	const { remaining, percentage } = useTierCountdown(
		lead?.expiresAt,
		handleExpire,
		lead?._id,
	);

	const handleSkip = useCallback(() => {
		dispatch(removeFreshLeadPool(lead._id));
		toast.info('Lead request skipped');
	}, [dispatch, lead?._id]);

	const handleSkipAll = useCallback(() => {
		dispatch(clearAllLeadPoolFreshLeads());
		toast.info('All lead requests skipped');
	}, [dispatch]);

	// Format long text with truncation
	const formatLongText = (text, maxLength = 25) => {
		if (!text) return 'N/A';
		return text.length > maxLength
			? `${text.substring(0, maxLength)}...`
			: text;
	};

	return (
		<MotionVStack
			spacing={3}
			align='stretch'
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}
			mb={4}
		>
			{/* Compact Progress Bar */}
			<Progress
				value={percentage}
				size='xs'
				w='full'
				borderRadius='full'
				bg='bg.elevated'
				sx={{
					'& > div': {
						transition: 'width 0.1s linear',
					},
				}}
				colorScheme={remaining <= 5 ? 'red' : 'green'}
			/>

			{/* Main Lead Card - Compact */}
			{lead?.isFinalized ? (
				<Box>
					<Alert
						status='error'
						variant='left-accent'
						borderRadius='md'
						fontSize='sm'
					>
						<AlertIcon />
						<Box>
							<AlertTitle>Lead Finalized</AlertTitle>
							<AlertDescription>
								This lead has already been responsed by another admin.
							</AlertDescription>
						</Box>
					</Alert>
				</Box>
			) : (
				<>
					<Box
						p={4}
						bg='bg.surface'
						borderRadius='xl'
						borderWidth='1px'
						borderColor='border.default'
						boxShadow='card'
						position='relative'
						overflow='hidden'
						_hover={{ borderColor: 'gold.primary', boxShadow: 'md' }}
						transition='all 0.2s'
						sx={{
							'&::-webkit-scrollbar': {
								display: 'none',
							},
							scrollbarWidth: 'none',
						}}
					>
						<VStack spacing={3} align='stretch'>
							{/* Header with Lead Name and Quick Actions */}
							<HStack justify='space-between' align='center'>
								<VStack align='start' spacing={0.5}>
									<Heading size='xs' noOfLines={1} color='text.heading'>
										{lead?.leadName || 'New Lead'}
									</Heading>
								</VStack>

								{/* Time Indicator */}
								<HStack
									spacing={1}
									bg='bg.elevated'
									px={2}
									py={1}
									borderRadius='full'
								>
									<Icon as={FiClock} size={12} color='text.muted' />
									<Text fontSize='2xs' fontWeight='medium' color='text.body'>
										{remaining > 0 ? `${remaining}s left` : 'Expired'}
									</Text>
								</HStack>
							</HStack>

							{/* Lead Details - Compact Grid */}
							<SimpleGrid columns={1} spacing={2}>
								<CompactInfoItem
									icon={FiDollarSign}
									label='Budget'
									value={formatLongText(
										lead?.budget || lead?.leadData?.budget,
										30,
									)}
									tooltip={lead?.budget || lead?.leadData?.budget}
									color='green.500'
								/>
								<CompactInfoItem
									icon={FiTarget}
									label='Campaign'
										value={formatLongText(
										lead?.leadCampaign || lead?.leadData?.leadCampaign,
										25,
									)}
									tooltip={lead?.leadCampaign || lead?.leadData?.leadCampaign}
									color='blue.500'
								/>
								<CompactInfoItem
									icon={FiGlobe}
									label='Ad Name'
									value={formatLongText(
										lead?.leadSourceDetails ||
											lead?.leadData?.leadSourceDetails,
										25,
									)}
									tooltip={
										lead?.leadSourceDetails || lead?.leadData?.leadSourceDetails
									}
									color='purple.500'
								/>
								<CompactInfoItem
									icon={FaStar}
									label='Interest'
									tooltip={lead?.interest || lead?.leadData?.interest}
									tooltip={lead?.interest}
									color='orange.500'
								/>
								<CompactInfoItem
									icon={FaUser}
									label='Requested By'
									value={formatLongText(lead?.agentName, 35)}
									tooltip={lead?.agentName}
									color='cyan.500'
								/>
							</SimpleGrid>

							{/* Action Buttons - Compact */}
							<AnimatePresence>
								{remaining > 0 ? (
									<VStack gap='2' w='full'>
										<HStack spacing={2} w='full'>
											<Button
												flex={2}
												h='36px'
												fontSize='sm'
												borderRadius='lg'
												bg='green.600'
												color='white'
												_hover={{ bg: 'green.700' }}
												isLoading={isSubmitting}
												isDisabled={isSubmitting}
												onClick={() => handleSubmit(lead._id, 'approve')}
												size='sm'
											>
												Approve
											</Button>
											<Button
												flex={2}
												h='36px'
												fontSize='sm'
												borderRadius='lg'
												bg='red.600'
												color='white'
												_hover={{ bg: 'red.700' }}
												isLoading={isSubmitting}
												isDisabled={isSubmitting}
												onClick={() => handleSubmit(lead._id, 'reject')}
												size='sm'
											>
												Reject
											</Button>
										</HStack>
										<Button
											// h='36px'
											w='full'
											fontSize='sm'
											borderRadius='lg'
											variant='outline'
											colorScheme='gray'
											leftIcon={<Icon as={FiX} size={16} />}
											onClick={handleSkip}
											isDisabled={isSubmitting}
											size='sm'
										>
											Skip
										</Button>
											{hasMultipleLeads && (
											<Button
												w='full'
												fontSize='sm'
												borderRadius='lg'
												variant='outline'
												colorScheme='red'
												leftIcon={<Icon as={FiX} size={16} />}
												onClick={handleSkipAll}
												isDisabled={isSubmitting}
												size='sm'
											>
												Skip All
											</Button>
										)}
									</VStack>
								) : (
									<MotionBox
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										w='full'
									>
										<Button
											w='full'
											h='36px'
											fontSize='sm'
											borderRadius='lg'
											isDisabled
											bg='bg.elevated'
											color='text.muted'
											leftIcon={<Icon as={FiClock} size={16} />}
											size='sm'
										>
											Lead Expired
										</Button>
									</MotionBox>
								)}
							</AnimatePresence>
						</VStack>
					</Box>
				</>
			)}
		</MotionVStack>
	);
};

// Compact Info Item Component
const CompactInfoItem = ({ icon, label, value, tooltip, color }) => (
	<HStack spacing={1.5} align='center' bg='bg.elevated' p={1.5} borderRadius='md'>
		<Icon as={icon} boxSize={3} color={color} />
		<VStack spacing={0} align='start' flex={1}>
			<Text fontSize='xs' color='text.muted' lineHeight='1'>
				{label}
			</Text>
			<Tooltip label={tooltip} hasArrow placement='top' openDelay={300}>
				<Text fontSize='sm' fontWeight='medium' noOfLines={1} color='text.body'>
					{value || 'N/A'}
				</Text>
			</Tooltip>
		</VStack>
	</HStack>
);

export default FreshLeadPoolCard;
